<template>
  <div class="submit-resume-container">
    <div class="header-container">
      <span class="title">{{ $t(`${i18nPrefix}.label.submitTitle`) }}</span>
      <span class="tips">{{ $t(`${i18nPrefix}.tips.submitTips`) }}</span>
    </div>
    <div class="body-container">
      <WrappedForm
        ref="form"
        class="candidate-base-info"
        label-width="140px"
        :model="stepData"
        :formLines="formLines"
      >
        <template #uploadResume>
          <div class="upload-resume-content">
            <div class="upload-dragger" @dragover.prevent="handleDragOver" @drop.prevent="handleDrop" @click="handleUploadFile">
              <div class="upload-icon">
                <i class="iconfont iconupload"></i>
              </div>
              <div class="drop-tip">{{ $t(`${i18nPrefix}.tips.uploadResume`) }}<label class="browser-file-label">{{ $t(`${i18nPrefix}.label.upload`) }}</label></div>
              <div class="type-tips">{{ $t(`${i18nPrefix}.tips.resumeTypeTips`) }}</div>
              <div v-show="false" class="browser-file">
                <input
                  ref="browserFilesInput"
                  type="file"
                  class="browser-file-input"
                  :accept="inputAcceptFileType"
                  @change="handleBrowserFilesChange"
                />
              </div>
              <!-- <div class="upload-tip">{{ $t(`${i18nPrefix}.message.uploadTip`) }}</div> -->
              <!-- <div class="upload-tip">
                <div v-for="(item, index) in $t(`${i18nPrefix}.message.uploadTips`)" class="tip-block" :key="index">{{ item }}</div>
              </div> -->
            </div>
            <div class="file" v-if="stepData.resumeFile">
              <div class="file-icon"></div>
              <div class="file-name" >{{ stepData.resumeFile.name }}</div>
              <div class="remove-btn" @click="handleDeleteFile">
                <i class="el-icon-close"></i>
              </div>
            </div>
          </div>
      </template>
      </WrappedForm>
    </div>
    <div class="policy" :class="isHeadhunterAccount && 'is-headhunter'">
      {{ $t(`${this.i18nPrefix}.tips.${isHeadhunterAccount ? 'agencySubmitPolicy' : 'candidateSubmitPolicy'}`) }}
    </div>
  </div>
</template>

<script>

// import _ from 'lodash'
import mixin from './mixin'
import { mapActions, mapGetters } from 'vuex'
import { validateEmail, validatePhoneByRegexpList } from '@/utils/validator'
import WrappedForm from '@/components/formEditor/wrappedForm.vue'
import { MAX_UPLOAD_RESUME_FILE_SIZE } from '@/constant/application'

export default {
  mixins: [mixin],
  components: {
    WrappedForm
  },
  props: {
    setting: {
      type: Object,
      required: false
    }
  },
  data () {
    return {
      i18nPrefix: 'personal.resume',
      stepData: {
        resumeFile: null
      },
      acceptFileTypes: [
        'text/html',
        'application/pdf',
        'application/msword', // .doc
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
        'image/png',
        'image/jpeg',
        'image/jpg',
        'image/bmp'
      ],
      hasFileUpload: false,
      limit: 1,
      fileList: [],
      uploadPrefix: 'personal/referral/submit/',
      formErrors: {
        email: {
          checked: null,
          error: null
        },
        mobile: {
          checked: null,
          error: null
        }
      }
    }
  },
  computed: {
    ...mapGetters('account', ['isHeadhunterAccount']),
    inputAcceptFileType () {
      return this.acceptFileTypes.join(',')
    },
    needUploadResume () {
      if (this.setting && this.setting.needUploadResume) {
        return true
      }
      return false
    },
    noNeedReason () {
      if (this.setting && this.setting.noNeedReason) {
        return false
      }
      return true
    },
    formLines () {
      const requiredMessage = this.$t('common.message.requiredFormField')
      const lines = {
        resumeFile: {
          elType: 'div',
          label: this.$t(`${this.i18nPrefix}.label.uploadResume`),
          contentSlot: 'uploadResume',
          rules: [{ required: this.needUploadResume, message: requiredMessage, trigger: 'change' }]
        },
        emailAddress: {
          class: 'email-form-line',
          label: this.$t(`${this.i18nPrefix}.label.emailAddress`),
          rules: [{ required: true, validator: this.validateEmailAddress, trigger: 'blur' }]
        },
        phoneNumber: {
          class: 'phone-form-line',
          label: this.$t(`${this.i18nPrefix}.label.phoneNumber`),
          rules: [{ required: true, validator: this.validateMobile, trigger: 'blur' }]
        },
        firstName: {
          label: this.$t(`${this.i18nPrefix}.label.firstName`),
          rules: [{ required: true, message: requiredMessage, trigger: 'change' }]
        },
        lastName: {
          label: this.$t(`${this.i18nPrefix}.label.lastName`),
          rules: [{ required: true, message: requiredMessage, trigger: 'change' }]
        },
        currentCompany: {
          label: this.$t(`${this.i18nPrefix}.label.currentCompany`)
        },
        currentPosition: {
          label: this.$t(`${this.i18nPrefix}.label.currentPosition`)
        },
        referralReason: {
          class: 'referral-reason-form-line',
          props: {
            type: 'textarea',
            autosize: { minRows: 3, maxRows: 3 },
            resize: 'none',
            maxlength: 2000
          },
          label: this.$t(`${this.i18nPrefix}.label.referralReason`),
          rules: [{ required: this.noNeedReason, message: requiredMessage, trigger: 'change' }]
        }
      }
      return lines
    }
  },
  async created () {
  },
  methods: {
    ...mapActions('common', ['getQiniuUploadToken']),
    ...mapGetters('account', ['getCurrentAccount']),
    ...mapActions('referral', ['getReferralUploadedCandidateCount']),
    async validateEmailAddress (rule, value, callback) {
      const formError = this.formErrors.email
      const checked = formError.checked === value
      if (checked) {
        if (formError.error) {
          callback(new Error(formError.error))
        } else {
          callback()
        }
        return
      }
      let errMsg = null
      try {
        errMsg = validateEmail(rule, value)
        if (errMsg) {
          callback(new Error(errMsg))
          return
        }
        const email = value
        const phone = this.stepData.phoneNumber
        const hasInTalentPool = await this.hasInTalentPool(email, phone)
        if (hasInTalentPool) {
          errMsg = this.$t(`${this.i18nPrefix}.message.hasUploadedInTalentPool`)
          callback(new Error(errMsg))
          return
        }
        this.$refs.form.clearValidate('phoneNumber')
        callback()
      } finally {
        formError.checked = value
        formError.error = errMsg
      }
    },
    async validateMobile (rule, value, callback) {
      const formError = this.formErrors.mobile
      const checked = formError.checked === value
      if (checked) {
        if (formError.error) {
          callback(new Error(formError.error))
        } else {
          callback()
        }
        return
      }
      let errMsg = null
      try {
        errMsg = validatePhoneByRegexpList(rule, value)
        if (errMsg) {
          callback(new Error(errMsg))
          return
        }
        const phone = value
        const email = this.stepData.emailAddress
        const hasInTalentPool = await this.hasInTalentPool(email, phone)
        if (hasInTalentPool) {
          errMsg = this.$t(`${this.i18nPrefix}.message.hasUploadedInTalentPool`)
          callback(new Error(errMsg))
          return
        }
        this.$refs.form.clearValidate('emailAddress')
        callback()
      } finally {
        formError.checked = value
        formError.error = errMsg
      }
    },
    async hasInTalentPool (email, phone) {
      if (!email && !phone) {
        return
      }
      const params = {
        email: email,
        phone: phone,
        job_id: this.$route.query.job_id
      }
      const uploadedCount = await this.getReferralUploadedCandidateCount(params)
      return uploadedCount && uploadedCount > 0
    },
    async setStepDataToCandidateInfo () {
      const stepData = this.stepData
      const candidateInfo = this.candidateInfo
      this.$set(candidateInfo, 'referralData', {
        resumeFile: stepData.resumeFile,
        emailAddress: stepData.emailAddress,
        phoneNumber: stepData.phoneNumber,
        firstName: stepData.firstName,
        lastName: stepData.lastName,
        currentCompany: stepData.currentCompany,
        currentPosition: stepData.currentPosition,
        referralReason: stepData.referralReason
      })
    },
    async getStepData () {
      const referralData = this.candidateInfo.referralData || {}
      const stepData = {
        emailAddress: referralData.emailAddress,
        phoneNumber: referralData.phoneNumber,
        firstName: referralData.firstName,
        lastName: referralData.lastName,
        currentCompany: referralData.currentCompany,
        currentPosition: referralData.currentPosition,
        referralReason: referralData.referralReason,
        resumeFile: null
      }

      return stepData
    },
    handleBrowserFilesChange ($event) {
      this.addSelectFile($event.target?.files || this.refs.browserFilesInput.files)
    },
    handleDragOver ($event) {

    },
    handleDrop ($event) {
      this.addSelectFile($event.dataTransfer?.files)
    },
    handleDeleteFile () {
      this.$refs.browserFilesInput.value = null
      this.stepData.resumeFile = null
    },
    handleUploadFile () {
      this.$refs.browserFilesInput.click()
    },
    addSelectFile (files) {
      if (!files) {
        return
      }
      const file = files[0]
      if (file.size > MAX_UPLOAD_RESUME_FILE_SIZE) {
        this.$message.warning(this.$t('admin.application.candidate.message.fileSizeLimit', { size: 10 }))
        return
      }
      this.stepData.resumeFile = file
    }
  }
}
</script>

<style lang="scss" scoped>
.submit-resume-container {
  width: 600px;
  margin: auto;
  overflow: auto;
  background: #f8f8f8;

  .header-container {
    padding-bottom: 30px;

    > span {
      display: block;
      font-size: 12px;
      height: 17px;
      line-height: 17px;
      font-weight: 400;
      color: #909399;
    }

    .title {
      color: #0096cc;
      font-size: 18px;
      height: 25px;
      line-height: 25px;
      font-weight: 500;
      margin-bottom: 12px;
    }
  }

  .body-container {
    background: #FFFFFF;
    padding: 30px 20px;

    .upload-resume-content {
      cursor: pointer;

      .upload-dragger {
        margin: auto;
        padding: 20px;
        border-radius: 8px;
        border: 1px dashed #74A4EF;
        text-align: center;
        color: #303133;

        .upload-icon {
          margin-top: 10px;

          i {
            font-size: 40px;
          }
        }

        .drop-tip {
          margin-top: 15px;
          height: 24px;
          line-height: 24px;
          font-weight: 400;
          font-size: 12px;

          .browser-file-label {
            color: #0096cc;
          }
        }

        .type-tips {
          height: 14px;
          font-size: 12px;
          font-weight: 400;
          color: #909399;
          margin-top: 2px;
          line-height: 14px;
        }

        .or-tip {
          margin-top: 10px;
          font-size: 18px;
        }

        .browser-file {
          margin-top: 10px;
          font-size: 18px;

          &-input {
            display: none;
          }
        }

        .upload-tip {
          margin-top: 15px;
          font-size: 14px;
        }
      }

      .file {
        display: flex;
        align-items: center;
        cursor: pointer;

        &:not(:last-child) {
          border-bottom: 1px solid #dcdcdc;
        }

        &:hover {
          .remove-btn {
            transform: rotateZ(180deg);
          }
        }

        .file-name {
          flex: 1;
          color: #000;
          white-space: nowrap;
          text-overflow: ellipsis;
          overflow: hidden;
        }

        .remove-btn {
          margin-left: auto;
          margin-right: 0;
          line-height: 1;
          font-size: 16px;
          transition: transform .3s;
        }
      }
    }

    .candidate-base-info ::v-deep {
      .email-form-line,
      .phone-form-line {
        &.is-error {
          margin-bottom: 0;

          .el-form-item__error {
            min-height: 22px;
            position: initial;
          }
        }
      }
    }
  }

  .policy {
    text-align: right;
    font-size: 14px;
    color: #303133;
    margin-top: 20px;
  }
}

.page-en {
  .policy {
    &.is-headhunter {
      text-align: left;
    }
  }
}
</style>
